import { Sparkles, MessageCircle, ArrowRight } from "lucide-react";

const LEMONBOT_PNG = "/assets/landing/lemonbot-character.png";

const CHATS = [
  { side: "left",  who: "Maya, Grade 7", text: "I'm scared no one will buy my lemonade.", testId: "lemonbot-chat-1" },
  { side: "right", who: "LemonBot",       text: "Totally normal! Let's start tiny — who is ONE neighbor you can ask first?", isBot: true, testId: "lemonbot-chat-2" },
  { side: "left",  who: "Marcus, Grade 9", text: "What should my price be?", testId: "lemonbot-chat-3" },
  { side: "right", who: "LemonBot",       text: "Cool — what do supplies cost per cup? Then we'll add a smart markup.", isBot: true, testId: "lemonbot-chat-4" },
];

/**
 * MeetLemonBotSection — bold black background, floating chat bubbles on the
 * left, glowing LemonBot character on the right. The point: AI is a COACH,
 * not a ghostwriter.
 */
export default function MeetLemonBotSection({ onLearnMore }) {
  return (
    <section
      id="lemonbot"
      className="relative bg-[#0A0A0A] text-white py-20 sm:py-28 overflow-hidden"
      data-testid="meet-lemonbot-section"
    >
      {/* Background glows */}
      <div aria-hidden="true" className="absolute top-1/4 -left-32 w-[32rem] h-[32rem] rounded-full bg-[#00E65C]/20 blur-3xl lsbp-pulse-glow" />
      <div aria-hidden="true" className="absolute bottom-0 -right-32 w-[32rem] h-[32rem] rounded-full bg-[#FFD500]/20 blur-3xl lsbp-pulse-glow" style={{ animationDelay: "2s" }} />

      <div className="relative max-w-7xl mx-auto px-4 lg:px-6 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Left — copy + chat bubbles */}
        <div>
          <div className="inline-flex items-center gap-2 bg-white/10 text-[#FFD500] px-3 py-1.5 rounded-full text-[11px] font-black tracking-widest uppercase mb-5">
            <Sparkles className="w-3.5 h-3.5" /> The Coach
          </div>
          <h2 className="text-[32px] md:text-5xl font-black tracking-tighter leading-[1.05]">
            Meet <span className="text-[#FFD500]">LemonBot</span>
            <sup className="text-base sm:text-xl text-gray-400 align-super">™</sup>
          </h2>
          <p className="mt-5 text-base md:text-xl text-gray-300 leading-relaxed max-w-md">
            The AI business coach for young entrepreneurs. LemonBot asks
            questions, brainstorms with students, and helps them find{" "}
            <em className="text-[#00E65C] not-italic font-bold">
              their own answers
            </em>{" "}
            — never writes the business plan for them.
          </p>

          {/* Floating chats */}
          <ul className="mt-8 space-y-3 max-w-md" aria-label="Sample coaching conversations">
            {CHATS.map((c, i) => (
              <li
                key={c.testId}
                className={`flex ${c.side === "right" ? "justify-end" : "justify-start"} lsbp-float`}
                style={{ animationDelay: `${i * 0.6}s`, animationDuration: "7s" }}
                data-testid={c.testId}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm shadow-lg ring-1 ${
                    c.isBot
                      ? "bg-[#FFD500] text-black ring-[#FFD500]/40 rounded-br-sm"
                      : "bg-white text-black ring-white/20 rounded-bl-sm"
                  }`}
                >
                  <p className="text-[10px] font-black uppercase tracking-widest mb-0.5 opacity-60">
                    {c.who}
                  </p>
                  <p className="font-semibold leading-snug">{c.text}</p>
                </div>
              </li>
            ))}
          </ul>

          <button
            onClick={onLearnMore}
            className="mt-8 inline-flex items-center gap-2 bg-[#FFD500] text-black px-6 py-3.5 rounded-full text-lg font-bold hover:bg-white transition-colors"
            data-testid="lemonbot-cta"
          >
            <MessageCircle className="w-4 h-4" /> See LemonBot in Action
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* Right — character */}
        <div className="relative flex items-center justify-center" aria-hidden="true">
          <div className="absolute inset-0 m-auto w-[80%] h-[80%] rounded-full bg-[#FFD500]/30 blur-3xl lsbp-pulse-glow" />
          <img
            src={LEMONBOT_PNG}
            alt="LemonBot — the AI business coach mascot for young entrepreneurs"
            className="relative w-full max-w-md lsbp-float drop-shadow-[0_30px_60px_rgba(255,213,0,0.25)]"
            loading="lazy"
            data-testid="lemonbot-character"
          />
        </div>
      </div>
    </section>
  );
}
